<template>
    <div class="table-container" v-if="productConfig">
        <table id="offer_table" class="table" @click="goToSpotOffers($event)">
            <thead v-if="Object.keys(lowestOffersPerTerminalAndNetwork).length > 0">
                <tr>
                    <th>Terminals</th>
                    <th>{{getProductName(productConfig.DieselId)}}</th>
                    <th>{{getProductName(productConfig.GasoilId)}}</th>
                    <th>{{getProductName(productConfig.KeroseneId)}}</th>
                    <th v-if="showOrderCaptureInfo"></th>
                </tr>
            </thead>   
            <tbody class="hover-grey">
                <tr v-if="getPrices.length < 1">
                    <td class="px-0">
                        <div class="alert alert-secondary mt-3 text-center">
                            <i class="fas fa-info-circle mr-2"></i>
                            Offers will not be displayed to buyers until Settlement Prices have been updated. Click on {{ settlementPricesButtonText }} to update Settlement Prices
                        </div>
                    </td>     
                </tr> 
                <tr v-else-if="Object.keys(lowestOffersPerTerminalAndNetwork).length < 1">
                    <td class="px-0" colspan="4">
                        <div class="alert alert-secondary mt-3 text-center">
                            <i class="fas fa-info-circle mr-2"></i>You have not created any offers for this market
                        </div>                        
                    </td>
                </tr>
                <tr v-else v-for="(products, terminalOrNetworkId) in lowestOffersPerTerminalAndNetwork" :key="terminalOrNetworkId" @click="goToSpotOffers()">
                    <td><strong>{{networkAndTerminalNames[terminalOrNetworkId]}}</strong></td>
                    <td v-if='products[productConfig.DieselId] != null' >
                      {{products[productConfig.DieselId] | priceformat}}
                    </td>
                    <td v-else>&ndash;.&mdash;</td>
                    <td v-if='products[productConfig.GasoilId] != null' >
                      {{products[productConfig.GasoilId] | priceformat}}
                    </td>
                    <td v-else>&ndash;.&mdash;</td>
                    <td v-if='products[productConfig.KeroseneId] != null' >
                      {{products[productConfig.KeroseneId] | priceformat}}
                    </td>
                    <td v-else>&ndash;.&mdash;</td>
                    <td v-if="showOrderCaptureInfo"> <button type="button" class="btn btn-link" @click="goToOrderCapture($event, terminalOrNetworkId)"> View </button> </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>


<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { _checkOffersAvailabilitySetting } from '@/util/offers.js';
import { getStraightProductId } from "@/util/products";
import { getCustomerSpotOffers } from "@/api/offers";

export default {
    data: function() {
      return {
        networkAndTerminalNames: {},
        buyerOffers: [],
      };
    },
    props:{
      selectedCustomer: {
        required: false,
        type: Object,
        default: () => {},
      },
    },
    watch:{
      selectedCustomer:{
        async handler(val){
          if(val.id){
            this.buyerOffers = await getCustomerSpotOffers(this.accountId, val.id);
          } else {
            this.buyerOffers = [];
          }
        },
      }
    },
    mounted: async function(){
      if(this.selectedCustomer.id){
        this.buyerOffers = await getCustomerSpotOffers(this.accountId, this.selectedCustomer.id);
      }
    },
    computed: {
        ...mapState('sellerOffers', ['liveTerminalPrices', 'offersAvailability']),
        ...mapGetters('fuelsType', ['getFuelsType', 'getSpotFuels']),
        ...mapGetters('login', ['getIsOrderCaptureEnabledForUser', 'accountId', 'isPricingEngineEnabled']),
        ...mapGetters('livePrices',['getPrices', 'settlementPricesButtonText']),
        ...mapGetters('metadata', ['productConfig', 'networkConfig', 'networks']),
        lowestOffersPerTerminalAndNetwork: function() {
          const spotOffers = this.showOrderCaptureInfo ? this.buyerOffers : this.liveTerminalPrices;
          return this.filterOffers(spotOffers);
        },
        showOrderCaptureInfo:{
          get: function(){
            return this.buyerOffers.length > 0 && this.getIsOrderCaptureEnabledForUser
          }
        }
    },
    methods: {
        ...mapActions('sellerTabs',['selectTab']),
        ...mapActions('terminals',['getTerminalFromId']),
        ...mapActions('orderCapture', ['setCustomer', 'setTerminal', 'setAllSpotProducts', 'setCurrentTab']),
        filterOffers: function(spotOffers){
          if (spotOffers.length < 1 || this.getPrices.length < 1 || !this.offersAvailability || !_checkOffersAvailabilitySetting("SPOT")) {
            return {};
          }
          const { DieselId, GasoilId, KeroseneId } = this.productConfig;
          const productColumns = [DieselId, GasoilId, KeroseneId];
          const offerLowestPriceMap = {};
            // Iterate through every offer, ensuring we are mapping the lowest price
            for (const offer of spotOffers) {
              const { terminal_id, networkId, fuel_id, type_id, price, livePrice, terminal, network } = offer;
              const productId = getStraightProductId(this.productConfig, fuel_id);

              // Only map prices for Diesel, Gasoil and Kero
              if (productColumns.includes(productId)) {
                let currentPrice;
                if(this.showOrderCaptureInfo){
                  currentPrice = price;
                } else{
                  currentPrice = (type_id == 'FIXED' || this.isPricingEngineEnabled) ? price : livePrice;
                }
                const terminalOrNetworkId = terminal_id || networkId;

                // Unique terminal or network not saved to map yet?
                if (!offerLowestPriceMap[terminalOrNetworkId])
                  offerLowestPriceMap[terminalOrNetworkId] = {};

                // Map unique Terminal Or Network Ids with their names
                if (!this.networkAndTerminalNames[terminalOrNetworkId]) 
                  this.updateNetworkAndTerminalNames(terminalOrNetworkId, terminal, network);

                // Ensuring we always store the lowest possible offer price for each terminal/network & product combination
                const currentMappedProductPrice = offerLowestPriceMap[terminalOrNetworkId][productId];
                if (!currentMappedProductPrice || currentMappedProductPrice > currentPrice)
                  offerLowestPriceMap[terminalOrNetworkId][productId] = currentPrice;
              }
            }
          return offerLowestPriceMap;
        },
        goToSpotOffers: function(){
          if(!this.showOrderCaptureInfo){
            /* Go to Manage Offers and select Spot tab */
            this.selectTab({ tabIndex:0, type:'Spot' }) 
            if(this.getPrices.length === 0) this.$store.dispatch("livePrices/editSettlementPrices"); 
            this.$router.push({ name: 'manageOffers' })
            //if router.push is 'manageOffers/spot' it duplicates manageOffers url and results in blank page
          }
        },
        goToOrderCapture: async function(event, terminalId){
          event.srcElement.disabled = true;

          this.setCurrentTab('SPOT');
          const terminal = await this.getTerminalFromId(terminalId);
          this.setTerminal(terminal);
          this.setCustomer(this.selectedCustomer);
          this.setAllSpotProducts();

          this.$router.push({ name: 'OrderCapture' })
        },
        getProductName(productId) {
          return this.getFuelsType.find(item => item._id === productId).name;
        },
        getNetworkName(networkId) {
          return this.networks.find(n => n._id === networkId).name;
        },
        updateNetworkAndTerminalNames(id, terminal) {
          let name = '';
          if (terminal) 
            name = terminal.name;
          else
            name = this.getNetworkName(id);
          this.networkAndTerminalNames[id] = name;
        }
    }
}
</script>
<style lang="scss" scoped>
  table {
    table-layout: fixed;
    width: 100%;
  }
</style>